import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '../types/auth'
import { AuthService } from '../services/auth/authService'
import { tokenManager } from '../services/auth/tokenManager'

interface AuthState {
  user: User | null
  isLoading: boolean
  error: string | null
  initialized: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  setUser: (user: User | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
  initialize: () => void
}

let authStateUnsubscribe: (() => void) | null = null

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      error: null,
      initialized: false,

      setUser: (user: User | null) => {
        set({ user })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      setError: (error: string | null) => {
        set({ error })
      },

      clearError: () => {
        set({ error: null })
      },

      initialize: () => {
        if (get().initialized) return

        set({ initialized: true })

        // Écouter les changements d'état d'authentification
        authStateUnsubscribe = AuthService.onAuthStateChanged((user) => {
          set({ user, isLoading: false })
        })
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const user = await AuthService.login(email, password)
          set({ user, isLoading: false, error: null })
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erreur de connexion'
          set({ isLoading: false, error: errorMessage })
          throw error
        }
      },

      logout: async () => {
        set({ isLoading: true })
        try {
          await AuthService.logout()
          // Clear token from memory
          tokenManager.clearToken()
          set({ user: null, isLoading: false })
          // Clear persisted state
          localStorage.removeItem('auth-storage')
        } catch (error) {
          console.error('Logout error:', error)
          set({ isLoading: false })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user }),
    }
  )
)

// Cleanup function
export const cleanup = () => {
  if (authStateUnsubscribe) {
    authStateUnsubscribe()
    authStateUnsubscribe = null
  }
}